import { AlertFactory } from 'alerts/Alert'
import {
  FindJobsQuery,
  JobScopeOption,
  JobStatus,
  JobWaitingOn,
  SearchableJobSortField,
} from 'jobs/dtos/job'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import { action, computed, observable, makeObservable } from 'mobx'
import {
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  firstValueFrom,
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeWhile,
} from 'rxjs/operators'
import {
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import {
  CursorPaged,
  CursorPageQuery,
  EncodedCursor,
  Id,
} from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { fromStream, toStream, tapAction } from 'utils/mobx-util'
import { areDeepEqual } from 'utils/object-util'
import { shareLatest, whileActive, withCancellation } from 'utils/rx-util'
import {
  DateRange,
  DateRangeFilter,
} from '../../infra/FilterControls/DateRange/DateRangeFilter'
import { JobWaitingOnFilterOption } from 'jobs/views/JobManagement/JobWaitingOnFilterOption'
import {
  JobPeriodOptions,
  JobScopeUpdatesOptions,
  ProgressionStepOptions,
  StatusOptions,
  WaitingOnOptions,
} from './JobsTableFilterValues'
import { JobsLoadFilterItemsAdapter } from './JobsLoadFilterItemsAdapter'
import SearchableJob from 'jobs/SearchableJob'
import JobSearchAdapter from './JobSearchAdapter'
import JobSearchQueryStringBuilder from './JobSearchQueryStringBuilder'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { TableSortOrder } from 'components/Table'
import {
  NumberRange,
  NumberRangeFilter,
} from 'infra/FilterControls/NumberRange/NumberRangeFilter'
import UserDataStore from 'iam/UserDataStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { SearchTextViewModel } from '../../infra/SearchTextViewModel'
import { JobProgressionStepStatus } from 'jobs/progression/JobProgression'
import { JobTicket } from '../tickets/JobTicket'
import { JobProgressionStepFilterOption } from 'jobs/views/JobManagement/JobProgressionFilterOption'
import Workspace from 'iam/models/Workspace'
import { match } from 'utils/pattern-match-util'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { FilterTeamMembers } from 'iam/TeamMemberStore'
import { InfiniteSelectFilter } from '../../infra/FilterControls/InfiniteSelect/InfiniteSelectFilter'
import { isInventorySupportingItemsEnabled } from '../../admin/inventory/inventory-supporting-items-toggle'
import { JobScopeChangeStatusFilterOption } from 'jobs/views/JobManagement/JobScopeChangeStatusFilterOption'
import { JobScopeChangeRequestStatus } from 'jobs/dtos/job-scope-change'
import { UserDataType } from 'iam/models/UserDataType'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import { JobPeriodFilterOption } from 'jobs/views/JobManagement/JobPeriodFilterOption'
import { FlagFilter } from '../../infra/FilterControls/Boolean/FlagFilter'
import { task } from 'mobx-task'

/**
 * Workspace filter for Jobs.
 */
export type JobWorkspaceFilter = AllWorkspaces | SpecificWorkspace

/**
 * Jobs in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Jobs in a specific workspace.
 */
export interface SpecificWorkspace {
  type: 'SpecificWorkspace'
  workspace: Workspace
}

/**
 * Job scope filter options.
 */
export enum JobScopeFilterOption {
  All,
  Mine,
}

export type JobFilter = {
  workspaces: Id[]
  jobType: Id[]
  jobStatus: JobStatusFilterOption[]
  progressionStep: JobProgressionStepFilterOption[]
  jobScopeUpdates: JobScopeChangeStatusFilterOption[]
  waitingOn: JobWaitingOnFilterOption[]
  createdRange: DateRange
  submittedRange: DateRange
  claimedRange: DateRange
  deadlineRange: DateRange
  closedRange: DateRange
  providerCutAmountRange?: NumberRange
  team: Id[]
  jobPeriod: JobPeriodFilterOption
  archived: boolean
  members: Id[]
  serviceItems: Id[]
}

/**
 * Implements job searching which is shared
 * between Job Management, Opportunities, and the Job List in Job Details.
 */
export default class JobSearchViewModel {
  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Used to trigger a search immediately with the current filter.
   */
  private searchTrigger = new Subject()

  /**
   * Backing field for the last search.
   */
  @observable.ref private _lastSearch: JobSearchResult = {
    jobs: [],
    searchFilter: JobSearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Total jobs for most recent search, plus cumulative job created events.
   */
  @observable totalJobs = 0

  /**
   * Whether the control is visible.
   */
  @observable visible = true

  /**
   * Sorting.
   */
  @observable sorting: JobSort = { type: 'Default' }

  /**
   * Filter by Collaborator Type
   */
  @observable collaboratorTypeFilter: JobCollaboratorTypeFilter = {
    type: 'ALL',
  }

  /**
   * Applies a date range to the date created filter.
   */
  @observable forcedCreatedDateRange: DateRange | null = null

  /**
   * Search text.
   */
  searchText = new SearchTextViewModel()

  /**
   * Default workspace.
   */
  defaultWorkspace = Workspace.none

  /**
   * Filter by scope.
   */
  @observable scope = JobScopeFilterOption.Mine

  filters = new FiltersViewModel<JobFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspaces: {
        title: 'Workspaces',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(
              this.loadFilterItemsAdapter.getWorkspaces(),
            ),
            true,
          ),
      },
      jobType: {
        icon: 'hashtag',
        title: 'Job type',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(key, title, async () => this._fetchLegends(), true),
      },
      jobStatus: {
        icon: 'invoices',
        title: 'Status',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(
              StatusOptions.filter((s) => {
                if (s.value === JobStatusFilterOption.InfoGathering) {
                  return isInventorySupportingItemsEnabled()
                }

                return true
              }),
            ),
            true,
          ),
      },
      progressionStep: {
        icon: 'progress',
        title: 'Progress',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(ProgressionStepOptions),
            true,
          ),
      },
      jobScopeUpdates: {
        icon: 'pending-approval',
        title: 'Job scope updates',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(JobScopeUpdatesOptions),
            true,
          ),
      },
      team: {
        icon: 'teams',
        title: 'Team',
        persist: true,
        factory: (key, title) =>
          new InfiniteSelectFilter(
            key,
            title,
            (paging, _, filter) => this._fetchUserTeams(paging, filter),
            true,
          ),
      },
      waitingOn: {
        icon: 'pending',
        title: 'Waiting on',
        persist: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(WaitingOnOptions),
          ),
      },
      jobPeriod: {
        title: 'Filter by period',
        implicit: true,
        persist: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            JobPeriodFilterOption.All,
            FilterItemSource.static(JobPeriodOptions),
          ),
      },
      createdRange: {
        icon: 'date-start',
        title: 'Date created',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      submittedRange: {
        icon: 'date-submitted',
        title: 'Date submitted',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      claimedRange: {
        icon: 'date-claimed',
        title: 'Date claimed',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      deadlineRange: {
        icon: 'date-created',
        title: 'Date deadline',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      closedRange: {
        icon: 'date-completed',
        title: 'Date completed',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      providerCutAmountRange: {
        icon: 'provider-cut',
        title: 'Payout',
        factory: (key, title) => new NumberRangeFilter(key, title, 'USD'),
      },
      archived: {
        icon: 'delete',
        title: 'Archived',
        implicit: true,
        factory: (key, title) => new FlagFilter(key, title),
      },
      members: {
        icon: 'add-people',
        title: 'Members',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(key, title, FilterItemSource.static([]), true),
      },
      serviceItems: {
        icon: 'ranking-money',
        title: 'Service Item',
        persist: true,
        factory: (key, title) =>
          new InfiniteSelectFilter(
            key,
            title,
            (paging, _, filter) => this._searchServiceItems(paging, filter),
            true,
          ),
      },
    },
  })

  /**
   * Jobs scroller.
   */
  scroller = new InfiniteScrollProvider<SearchableJob, JobSearchFilter>({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this._fetchJobs(
        toFindJobsQuery(paging, filter, this.forcedCreatedDateRange),
      ),
  })

  /**
   * Search filter changes.
   */
  private searchFilter$ = combineLatest([
    // Combine the search text changes...
    this.searchText.debouncedValue$,
    // ...with the remaining filter changes
    this.filters.appliedFilters$,
    // ...and the sort
    toStream(() => this.sorting, true),
    toStream(() => this.collaboratorTypeFilter, true),
    toStream(() => this.scope, true),
  ]).pipe(
    // Map what we've combined to a Search Filter.
    map(
      ([
        searchText,
        appliedFilters,
        sort,
        collaboratorType,
        scope,
      ]): JobSearchFilter => {
        return {
          sort,
          collaboratorType,
          searchText: searchText ?? '',
          workspaceIds: appliedFilters.workspaces,
          jobType: appliedFilters.jobType,
          status: appliedFilters.jobStatus ?? null,
          progressionStep: appliedFilters.progressionStep ?? null,
          jobScopeChangeStatus: appliedFilters.jobScopeUpdates ?? null,
          waitingOn: appliedFilters.waitingOn?.[0] ?? null,
          createdDateRange: appliedFilters.createdRange ?? null,
          transmittedDateRange: appliedFilters.submittedRange ?? null,
          acceptedDateRange: appliedFilters.claimedRange ?? null,
          deadlineDateRange: appliedFilters.deadlineRange ?? null,
          closedDateRange: appliedFilters.closedRange ?? null,
          providerCutAmountRange: appliedFilters.providerCutAmountRange ?? null,
          scope: scope ?? JobScopeFilterOption.Mine,
          period: appliedFilters.jobPeriod ?? JobPeriodFilterOption.All,
          team: appliedFilters.team,
          archived: appliedFilters.archived ?? false,
          memberIds: appliedFilters.members,
          serviceItems: appliedFilters.serviceItems ?? [],
        }
      },
    ),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  searches$ = merge(
    // When the search filter changes
    this.searchFilter$,
    // Or when we explicitly request a search for the current filter (can be used to skip debouncing)
    this.searchTrigger.pipe(
      // Since searchFilter$ is using replay, it will emit the last
      // search filter immediately on subscribe. So smart. ＼（＾ ＾）／
      switchMap(() => this.searchFilter$),
    ),
  ).pipe(
    // Start a non-filtered search immediately.
    startWith(JobSearchFilter.none),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<JobSearchFilter>(JobSearchFilter.equals),
    // Start a search, return the filter that was used.
    switchMap(
      (query): Observable<JobSearchResult> =>
        withCancellation(async (ct) => {
          let result = await this.scroller.fetch(query)
          // Keep loading more until we get something back that isn't being filtered out
          // on the client. If we don't do this here, we potentially display an empty state
          // which does not trigger infinite scroll.
          while (this.jobs.length === 0 && !this.scroller.isDone) {
            ct.throwIfCancelled()
            result = await this.scroller.more()
          }

          return {
            searchFilter: query,
            filtersApplied: !JobSearchFilter.isNone(query),
            total: result?.total || this.jobs.length,
            jobs: this.jobs,
          }
        }),
    ),
    // check if the user can view service item filter
    tapAction((filters) => {
      this.toggleEnableServiceItemFilter()
      return filters
    }),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  /**
   * Constructor.
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private loadFilterItemsAdapter: JobsLoadFilterItemsAdapter,
    private ticketsAdapter: TicketsAdapter,
    private newAlert: AlertFactory,
    // private jobUpdates$: Observable<FeathersEvent<Job>>,
    // The consumer can apply a transform to the list of jobs, such as
    // filtering and sorting.
    private listTransformer = (
      jobs: readonly SearchableJob[],
    ): readonly SearchableJob[] => jobs,
    private getCurrentMember: GetCurrentMember,
    private userDataStore: UserDataStore,
    private getCurrentUserMemberships: GetCurrentUserWorkspaceMemberships,
    private filterTeamMembers: FilterTeamMembers,
    private filterStorage?: FilterStorage,
  ) {
    makeObservable(this)
  }

  /**
   * The internal jobs list ran through the transformer.
   */
  @computed
  get jobs() {
    return this.listTransformer(uniq(this.scroller.items))
  }

  @computed
  get isUserTypePro() {
    const member = this.getCurrentMember()
    const userData = this.userDataStore.getByPublicId(member.user.publicId)
    return userData?.userDataType === UserDataType.Pro
  }

  /**
   * Collect all filtered jobs for export.
   */
  getAllFilteredJobs = async (): Promise<SearchableJob[]> => {
    const searchFilters = await firstValueFrom(this.searchFilter$, {
      defaultValue: undefined,
    })
    let cursor: EncodedCursor | null = null
    const result: SearchableJob[] = []
    do {
      const page: CursorPaged<SearchableJob> = await this._fetchJobs(
        toFindJobsQuery(
          { limit: 100, after: cursor },
          searchFilters,
          this.forcedCreatedDateRange,
        ),
      )
      result.push(...page.data)
      cursor = page.cursor
    } while (cursor !== null && cursor.length > 0)

    return result
  }

  /**
   * Last search computed getter.
   */
  @computed
  get lastSearch() {
    return this._lastSearch
  }

  /**
   * Gets the current search state.
   */
  @computed
  get searchState(): SearchState {
    if (this.getIsFirstFetchRunning()) {
      return SearchState.InitialSearchRunning
    }

    if (this.lastSearch.jobs.length === 0 && this.scroller.isDone) {
      return this.lastSearch.filtersApplied
        ? SearchState.EmptyButFilterApplied
        : SearchState.CompletelyEmpty
    }

    return SearchState.Some
  }

  /**
   * Determines if we should use the search jobs for an admin.
   */
  @computed
  get useSearchJobsForAdmin(): boolean {
    const hasPermission = this.getCurrentUserMemberships().some((m) =>
      m.member.hasPermission('ADMIN_JOB_LIST'),
    )

    return (
      hasPermission &&
      getFeatureToggleClient().variation(
        'WorkPortal.UseSearchJobsForAdmin',
        false,
      )
    )
  }

  /**
   * Shows the search scope filter.
   */
  @computed
  get showSearchScopeFilter() {
    return this.useSearchJobsForAdmin
  }

  /**
   * Gets the workspaces filter.
   */
  @computed
  get workspacesFilter(): SelectFilter<string, Id> {
    return this.filters.getFilter('workspaces') as SelectFilter<string, Id>
  }

  /**
   * Gets the job period filter.
   */
  @computed
  get jobPeriodFilter() {
    return this.filters.getFilter('jobPeriod') as SingleSelectFilter<
      string,
      JobPeriodFilterOption
    >
  }

  /**
   * Gets the job period option.
   */
  @computed
  get jobPeriodOption() {
    return this.jobPeriodFilter.value
  }

  /**
   * Validate if the user can list service items
   */
  canListServiceItems(): boolean {
    const member = this.getCurrentMember()
    if (member.user.platformAdmin) {
      return true
    }

    const workspaces = this.filters.appliedFilters.workspaces
    if (workspaces && workspaces.length > 0) {
      const workspaceFilter = this.workspacesFilter
      const workspaceSet = new Set(workspaceFilter.selected)

      return this.getCurrentUserMemberships()
        .filter((m) => workspaceSet.has(m.workspace.id))
        .every((m) =>
          m.member.hasPermission('MANAGE_WORKSPACE_INVENTORY_ITEMS'),
        )
    }

    return member.hasPermission('ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS')
  }

  toggleEnableServiceItemFilter() {
    if (!this.canListServiceItems()) {
      this.filters.removeFilter('serviceItems')
    } else {
      const filter = this.filters.availableFilters.some(
        (f) => f.key === 'serviceItems',
      )
      if (
        !filter &&
        this.filters.availableFilterRegistrations.some(
          (f) => f.key === 'serviceItems',
        )
      ) {
        this.filters.addFilter('serviceItems')
      }
    }
  }

  /**
   * Activates the view model.
   */
  activate() {
    // Set the default workspace.
    this.defaultWorkspace = this.getCurrentMember().workspace ?? Workspace.none

    if (!this.isUserTypePro) {
      this.filters.removeFilter('providerCutAmountRange')
    }

    if (this.useSearchJobsForAdmin) {
      this.scope = JobScopeFilterOption.All
    }

    // Activate the filters
    this.filters.activate()

    // Only update the job if a filter isn't present.
    const jobUpdates$ = this.jobFetcher.events$.pipe(
      whileActive(
        this.searches$.pipe(map((e) => JobSearchFilter.isNone(e.searchFilter))),
      ),
    )
    this.subscription = merge(
      // Search requests.
      this.searches$.pipe(
        // Can't use fromStream because it will subscribe immediately,
        // which we don't want.
        tapAction((search) => {
          this._lastSearch = search
          this.totalJobs = search.total
        }),
      ),

      // When jobs are added/updated, make sure they are in the list
      jobUpdates$.pipe(
        mergeMap((e) => {
          const publicId = this.getCurrentMember().user.publicId
          const isCollaborator =
            e.job.members.some((m) => m.id === publicId) ||
            this.filterTeamMembers(publicId, e.job.clientTeamId).length > 0

          if (!e.job.archived) {
            return of(e).pipe(
              filter((e) => {
                return (
                  !this.scroller.items.includes(e.job) &&
                  (isCollaborator || this.scope === JobScopeFilterOption.All)
                )
              }),
              tapAction((e) => {
                this.scroller.items.unshift(e.job)
                this.totalJobs++
              }),
            )
          } else {
            return of(e).pipe(
              filter(
                (e) =>
                  this.scroller.items.includes(e.job) &&
                  (isCollaborator || this.scope === JobScopeFilterOption.All),
              ),
              tapAction((e) => {
                const index = this.scroller.items.findIndex(
                  (j) => j.id === e.job.id,
                )
                this.scroller.items.splice(index, 1)
                this.totalJobs--
              }),
            )
          }
        }),
      ),
    ).subscribe()
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
    this.forcedCreatedDateRange = null
  }

  /**
   * Sets the forced created date range.
   *
   * @param range
   */
  @action.bound
  setForcedCreatedDateRange(range: DateRange | null) {
    this.forcedCreatedDateRange = range
  }

  /**
   * Sets the job collaborator type filter.
   *
   * @param collaboratorTypeFilter
   */
  @action.bound
  setCollaboratorTypeFilterMethod(
    collaboratorTypeFilter: JobCollaboratorTypeFilter,
  ) {
    this.collaboratorTypeFilter = collaboratorTypeFilter
  }

  /**
   * Sets the job sort.
   *
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: JobSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the job sort.
   *
   * @param field
   */
  @action.bound
  setSortField(field: SearchableJobSortField) {
    this.setSortMethod({
      field,
      type: 'Field',
      direction:
        this.sorting.type === 'Field'
          ? this.sorting.direction
          : SortDirection.Ascending,
    })
  }

  /**
   * Sets the job scope for the search.
   *
   * @param {JobScopeFilterOption} scope
   */
  @action.bound
  setScope(scope: JobScopeFilterOption) {
    this.scope = scope
  }

  /**
   * Sets the job period for the search.
   *
   * @param {JobPeriodFilterOption} period
   */
  @action.bound
  setJobPeriod(period: JobPeriodFilterOption) {
    this.jobPeriodFilter.change(period)
  }

  /**
   * Sets the job sort.
   *
   * @param direction
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    if (this.sorting.type !== 'Field') {
      return
    }

    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ type: 'Default' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
      type: 'Field',
    })
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Removes the jobs with the given IDs from the scroller.
   */
  @action.bound
  removeJobs(ids: string[]) {
    this.scroller.remove((job) => ids.includes(job.id))
    this.totalJobs -= ids.length
  }

  /**
   * Fetches all jobs from the scroller.
   */
  fetchAllJobs = task.resolved(async () => {
    if (this.scroller.isDone) {
      return
    }

    this.scroller.limit = 250

    while (!this.scroller.isDone) {
      await this.scroller.more()

      // Timeout for 500ms to prevent overwhelming dotnet.
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    this.scroller.limit = 20
  })

  /**
   * Fetch jobs.
   *
   * @param params
   */
  private async _fetchJobs(
    params: Omit<FindJobsQuery, 'all'>,
  ): Promise<CursorPaged<SearchableJob>> {
    try {
      const page = await this.jobFetcher.searchJobs({
        ...params,
        all: true,
      })

      // Retrieve latest open tickets for the loaded jobs.
      // We purposefully don't wait for this.
      this._fetchTicketsForJobs(page.data).then()

      return page
    } catch (error) {
      this.newAlert()
        .fromError(error)
        .withActions({
          title: 'Try again',
          handler: () => this.searchTrigger.next(undefined),
        })
        .show()
      console.error(error)
      return CursorPaged.empty
    }
  }

  /**
   * Fetch legends based on filtered workspaces.
   *
   * @returns
   */
  private _fetchLegends() {
    const ids =
      this.workspacesFilter.selected.length > 0
        ? this.workspacesFilter.selected
        : this.loadFilterItemsAdapter.getWorkspaces().map((w) => w.value)
    return this.loadFilterItemsAdapter.fetchLegends(ids)
  }

  private _searchServiceItems(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ) {
    return this.loadFilterItemsAdapter.fetchServiceItems(
      this.workspacesFilter.selected,
      paging,
      filter?.searchText as string,
    )
  }

  /**
   * Fetch all teams across workspaces.
   *
   * @returns
   */
  private _fetchUserTeams(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ) {
    const member = this.getCurrentMember()

    return this.loadFilterItemsAdapter.fetchTeams({
      userPublicId: Id.assert(member.user.publicId),
      ...paging,
      ...filter,
      workspaceIds: this.workspacesFilter.value,
    })
  }

  /**
   * Retrieves open tickets for the given jobs.
   * @param jobs
   * @private
   */
  private async _fetchTicketsForJobs(jobs: SearchableJob[]) {
    try {
      const ids = jobs.map((job) => job.id)
      await this.ticketsAdapter.fetchLatestOpenTicketsForJobs(ids)
    } catch (err: unknown) {
      console.error('Unable to retrieve open tickets for jobs', err)
    }
  }

  /**
   * Subscribes to the pending state of the fetch task.
   * Once it flips to `false`, stop.
   */
  private getIsFirstFetchRunning = fromStream(
    true,
    toStream(() => this.scroller.fetch.pending, true).pipe(
      takeWhile(Boolean, true),
    ),
  )
}

/**
 * Maps a `JobScopeFilterOption` to a `JobScopeOption`
 */
function toJobScopeOption(scope?: JobScopeFilterOption) {
  if (scope === undefined) {
    return JobScopeOption.Mine
  }

  return match(scope, {
    [JobScopeFilterOption.All]: () => JobScopeOption.All,
    [JobScopeFilterOption.Mine]: () => JobScopeOption.Mine,
  })
}

/**
 * Maps an array of `JobStatusFilterOption` to an array of `JobStatus`
 *
 * @param jobStatusFilterOptions
 */
function toJobStatuses(
  jobStatusFilterOptions?: JobStatusFilterOption[] | null,
): JobStatus[] | undefined {
  return jobStatusFilterOptions?.flatMap((jobStatusFilterOption) => {
    switch (jobStatusFilterOption) {
      case JobStatusFilterOption.Current:
        return ['CLAIMED', 'UNCLAIMED', 'UNDER_CONSTRUCTION']
      case JobStatusFilterOption.OnHold:
        return ['ON_HOLD']
      case JobStatusFilterOption.Idle:
        return ['IDLE']
      case JobStatusFilterOption.Closed:
        return ['CLOSED']
      case JobStatusFilterOption.InProgress:
        return ['CLAIMED']
      case JobStatusFilterOption.InfoGathering:
        return ['INFO_GATHERING']
      case JobStatusFilterOption.Unclaimed:
        return ['UNCLAIMED']
      case JobStatusFilterOption.Onboarding:
        return ['UNDER_CONSTRUCTION']
      default:
        return []
    }
  })
}

/**
 * Maps an array of `JobProgressionStepFilterOption` to an array of `JobProgressionStepStatus`
 *
 * @param progressionStepFilterOptions
 */
function toJobProgressionSteps(
  progressionStepFilterOptions?: JobProgressionStepFilterOption[] | null,
): JobProgressionStepStatus[] | undefined {
  return progressionStepFilterOptions?.map((filterOption) => {
    switch (filterOption) {
      case JobProgressionStepFilterOption.OpenItems:
        return JobProgressionStepStatus.OPEN_ITEMS
      case JobProgressionStepFilterOption.JobStarted:
        return JobProgressionStepStatus.JOB_STARTED
      case JobProgressionStepFilterOption.DraftInReview:
        return JobProgressionStepStatus.DRAFT_IN_REVIEW
      case JobProgressionStepFilterOption.DraftApproved:
        return JobProgressionStepStatus.DRAFT_APPROVED
      case JobProgressionStepFilterOption.DraftRejected:
        return JobProgressionStepStatus.DRAFT_REJECTED
      case JobProgressionStepFilterOption.AuthorizationRequested:
        return JobProgressionStepStatus.AUTHORIZATION_REQUESTED
      case JobProgressionStepFilterOption.AuthorizationSigned:
        return JobProgressionStepStatus.AUTHORIZATION_SIGNED
      case JobProgressionStepFilterOption.ReturnFiled:
        return JobProgressionStepStatus.RETURN_FILED
      case JobProgressionStepFilterOption.None:
        return JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED
      case JobProgressionStepFilterOption.NotStarted:
        return JobProgressionStepStatus.NOT_STARTED
      case JobProgressionStepFilterOption.PreparationCompleted:
        return JobProgressionStepStatus.PREPARATION_COMPLETED
      case JobProgressionStepFilterOption.ReviewCompleted:
        return JobProgressionStepStatus.REVIEW_COMPLETED
      case JobProgressionStepFilterOption.ExtensionInProgress:
        return JobProgressionStepStatus.EXTENSION_IN_PROGRESS
      case JobProgressionStepFilterOption.ExtensionConfirmed:
        return JobProgressionStepStatus.EXTENSION_CONFIRMED
      case JobProgressionStepFilterOption.ExtensionAccepted:
        return JobProgressionStepStatus.EXTENSION_ACCEPTED
      case JobProgressionStepFilterOption.ExtensionRejected:
        return JobProgressionStepStatus.EXTENSION_REJECTED
      case JobProgressionStepFilterOption.WaitingOnClient:
        return JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT
      case JobProgressionStepFilterOption.ReadyToFile:
        return JobProgressionStepStatus.READY_TO_FILE
      default:
        return JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED
    }
  })
}

/**
 * Maps an array of `JobScopeChangeStatusFilterOption` to an array of `JobScopeChangeStatus`
 *
 * @param jobScopeChangeStatusFilterOption
 */
function toJobScopeChangeStatus(
  jobScopeChangeStatusFilterOption?: JobScopeChangeStatusFilterOption[] | null,
): JobScopeChangeRequestStatus[] | undefined {
  if (!jobScopeChangeStatusFilterOption) {
    return undefined
  }

  const result: JobScopeChangeRequestStatus[] = []

  jobScopeChangeStatusFilterOption?.forEach((filterOption) => {
    switch (filterOption) {
      case JobScopeChangeStatusFilterOption.Accepted:
        result.push(JobScopeChangeRequestStatus.ACCEPTED)
        break
      case JobScopeChangeStatusFilterOption.Rejected:
        result.push(JobScopeChangeRequestStatus.REJECTED)
        result.push(JobScopeChangeRequestStatus.APPLY_FAILED)
        break
      case JobScopeChangeStatusFilterOption.PendingReview:
        result.push(JobScopeChangeRequestStatus.PENDING_FINALIZATION)
        result.push(JobScopeChangeRequestStatus.PENDING_ADMIN_APPROVAL)
        result.push(JobScopeChangeRequestStatus.PENDING_CLIENT_APPROVAL)
        break
      default:
        result.push(JobScopeChangeRequestStatus.UNSPECIFIED)
        break
    }
  })

  return result
}

/**
 * Maps `JobWaitingOnFilterOption` to `JobWaitingOn`
 *
 * @param jobWaitingOnFilterOptions
 */
function toJobWaitingOn(
  jobWaitingOnFilterOptions?: JobWaitingOnFilterOption | null,
): JobWaitingOn {
  if (jobWaitingOnFilterOptions === JobWaitingOnFilterOption.Client) {
    return 'Client'
  }
  if (jobWaitingOnFilterOptions === JobWaitingOnFilterOption.Provider) {
    return 'Provider'
  }
  return null
}

/**
 * Creates a `FindJobsQuery` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toFindJobsQuery(
  paging: CursorPageQuery,
  filter: JobSearchFilter | undefined,
  forcedCreatedDateRange: DateRange | null,
): FindJobsQuery {
  const query = {
    searchText: filter?.searchText,
    filter: new JobSearchQueryStringBuilder()
      .withWorkspaceIds(filter?.workspaceIds)
      .withLegendIds(filter?.jobType)
      .withStatuses(toJobStatuses(filter?.status))
      .withProgressionSteps(toJobProgressionSteps(filter?.progressionStep))
      .withJobScopeChangeStatus(
        toJobScopeChangeStatus(filter?.jobScopeChangeStatus),
      )
      .withinDateCreatedRange(
        forcedCreatedDateRange ?? filter?.createdDateRange,
      )
      .withinDateClosedRange(filter?.closedDateRange)
      .withinDateDeadlineRange(filter?.deadlineDateRange)
      .withinDateClaimedRange(filter?.acceptedDateRange)
      .withinDateSubmittedRange(filter?.transmittedDateRange)
      .withWaitingOn(toJobWaitingOn(filter?.waitingOn))
      .withProviderCutAmountRange(filter?.providerCutAmountRange)
      .withTeamIds(filter?.team)
      .withPeriod(filter?.period ?? JobPeriodFilterOption.All)
      .withArchivalStatus(filter?.archived)
      .withMemberIds(filter?.memberIds)
      .withServiceItems(filter?.serviceItems)
      .build(),
    $limit: paging.limit,
    after: paging.after,
    scope: toJobScopeOption(filter?.scope),
  } as FindJobsQuery

  if (filter?.sort.type === 'Field') {
    query.orderBy = `${[filter.sort.field]} ${
      filter.sort.direction === SortDirection.Descending ? 'desc' : 'asc'
    }`
  } else if (!query.searchText) {
    // Only apply the default sort when search text isn't provided
    query.orderBy = 'date_last_activity desc'
  }

  return query
}

/**
 * Adapter for retrieving ticket information.
 */
export interface TicketsAdapter {
  /**
   * Retrieves the latest open tickets for a list of jobs.
   */
  fetchLatestOpenTicketsForJobs(jobIds: Id[]): Promise<JobTicket[]>
}

/**
 * Possible values to filter by Collaborator Type.
 */
export type JobCollaboratorTypeFilter = {
  type: 'ALL' | 'CLIENT' | 'PROVIDER'
}

/**
 * Job sorting type.
 */
export type JobSort = DefaultSort | FieldSort

/**
 * Sort direction.
 */
export const enum SortDirection {
  Ascending = 'asc',
  Descending = 'desc',
  None = 'none',
}

/**
 * Default sort.
 */
export interface DefaultSort {
  type: 'Default'
}

/**
 * The possible fields to sort by.
 */
export type { JobSortingField } from 'jobs/dtos/job'

/**
 * Field sort.
 */
export interface FieldSort {
  type: 'Field'
  field: SearchableJobSortField
  direction: TableSortOrder
}

/**
 * Job search filter.
 */
export interface JobSearchFilter {
  searchText?: string
  workspaceIds?: Id[] | null
  jobType?: Id[] | null
  createdDateRange?: DateRange | null
  transmittedDateRange: DateRange | null
  acceptedDateRange: DateRange | null
  deadlineDateRange: DateRange | null
  closedDateRange: DateRange | null
  status: JobStatusFilterOption[] | null
  progressionStep: JobProgressionStepFilterOption[] | null
  jobScopeChangeStatus: JobScopeChangeStatusFilterOption[] | null
  waitingOn: JobWaitingOnFilterOption | null
  providerCutAmountRange?: NumberRange | null
  sort: JobSort
  collaboratorType: JobCollaboratorTypeFilter
  scope: JobScopeFilterOption
  period: JobPeriodFilterOption
  team?: Id[] | null
  archived: boolean
  memberIds?: Id[] | null
  serviceItems: Id[] | null
}

export const JobSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspaceIds: [],
    jobType: undefined,
    createdDateRange: null,
    transmittedDateRange: null,
    acceptedDateRange: null,
    deadlineDateRange: null,
    closedDateRange: null,
    status: null,
    progressionStep: null,
    jobScopeChangeStatus: null,
    waitingOn: null,
    providerCutAmountRange: null,
    sort: { type: 'Default' },
    collaboratorType: { type: 'ALL' },
    scope: JobScopeFilterOption.Mine,
    period: JobPeriodFilterOption.All,
    team: undefined,
    archived: false,
    memberIds: [],
    serviceItems: [],
  } as JobSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: JobSearchFilter) =>
    JobSearchFilter.equals(
      omit(JobSearchFilter.none, 'sort', 'scope'),
      omit(searchFilter, 'sort', 'scope'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (left: Partial<JobSearchFilter>, right: Partial<JobSearchFilter>) =>
    areDeepEqual(left, right),
}

/**
 * Search result.
 */
export interface JobSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: JobSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of jobs contained in the searcher.
   */
  readonly jobs: readonly SearchableJob[]
}

/**
 * Current state of searching.
 */
export const enum SearchState {
  /**
   * The initial search is currently running.
   */
  InitialSearchRunning,
  /**
   * The search returned something.
   */
  Some,
  /**
   * The search returned an empty result, but
   * there was a filter applied.
   */
  EmptyButFilterApplied,
  /**
   * The search returned an empty result
   * and no filters were applied.
   */
  CompletelyEmpty,
}
